import { Component, OnInit, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { CoreService } from '../../../services/core.service';
import { AiEngineService } from '../../../services/ai-engine.service';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AiEngineErrorDialogComponent } from '../../../components/ai-engine-error-dialog/ai-engine-error-dialog.component';

@Component({
  selector: 'app-ai-engine-new-content',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule
  ],
  templateUrl: './new-content.component.html',
  styleUrls: ['./new-content.component.scss']
})
export class AiEngineNewContentComponent implements OnInit, AfterViewInit {
  @ViewChild('aiModalContainer', { static: false }) aiModalContainer!: ElementRef;
  
  isProcessing = true;
  processingMessage = 'Loading AI Engine...';

  constructor(
    private coreService: CoreService,
    private aiEngineService: AiEngineService
  ) {}

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    // Automatically initialize the AI engine when the view loads
    setTimeout(() => {
      this.initializeAiEngine();
    }, 0);
  }

  private async initializeAiEngine(): Promise<void> {
    if (!this.aiModalContainer) {
      console.error('[AI Engine] Container not found');
      this.processingMessage = 'Error: Container not found';
      return;
    }

    const container = this.aiModalContainer.nativeElement;
    container.innerHTML = '<div class="w-full h-full flex items-center justify-center"><p>Loading AI Engine...</p></div>';

    // Store original request handlers
    const originalFetch = window.fetch;
    const originalXhrOpen = XMLHttpRequest.prototype.open;
    const originalXhrSend = XMLHttpRequest.prototype.send;

    try {
      // Intercept fetch requests
      window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
        console.group("[AI Engine][Fetch]");
        console.log("URL:", input);
        console.log("Method:", init?.method ?? "GET");
        console.log("Headers:", init?.headers);
        console.log("Body:", init?.body);
        console.groupEnd();

        return originalFetch.call(this, input, init);
      };

      // Intercept XHR requests
      XMLHttpRequest.prototype.open = function (method: string, url: string | URL) {
        console.group("[AI Engine][XHR Open]");
        console.log("Method:", method);
        console.log("URL:", url);
        console.groupEnd();

        return originalXhrOpen.call(this, method, url, true);
      };

      XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
        console.group("[AI Engine][XHR Send]");
        console.log("Body:", body);
        console.groupEnd();

        return originalXhrSend.call(this, body);
      };

      console.log("[AI Engine] Getting cloud context...");

      const context = await this.aiEngineService.getAiEngineContext().toPromise();
      const { cloudToken, uid } = context!;

      const cloudUrl = this.getCloudBaseUrl();
      console.log("[AI Engine] Cloud URL:", cloudUrl);

      console.log("[AI Engine] Importing bridge module...");

      const bridgeModule: any = await import(
        /* webpackIgnore: true */ `${cloudUrl}/lib/AiEngineBridge.js?v=${Date.now()}`
      );

      console.log("[AI Engine] Bridge module loaded:", bridgeModule);

      if (typeof bridgeModule.init !== "function") {
        throw new Error("AiEngineBridge.init is not a function");
      }

      if (typeof bridgeModule.mount_window !== "function") {
        throw new Error(
          "AiEngineBridge.mount_window is missing from named exports."
        );
      }

      console.group("[AI Engine] Initialization");
      console.log("UID:", uid);
      console.log("Cloud token exists:", Boolean(cloudToken));
      console.log("Asset types:", ["image", "text", "analysis", "video"]);
      console.log("Container:", container);
      console.groupEnd();

      bridgeModule.init("publio", cloudToken);

      console.log(
        "[AI Engine] Waiting for asset selection / Use selected..."
      );

      const assets = await bridgeModule.mount_window(
        { uid },
        ["image", "text", "analysis", "video"],
        container
      );

      /*
      * This will execute once mount_window returns
      * the selected assets, usually after "Use selected".
      */
      console.group("[AI Engine][Use selected] Assets received");
      console.log("Raw assets response:", assets);
      console.log("Assets type:", typeof assets);
      console.log("Is array:", Array.isArray(assets));

      if (Array.isArray(assets)) {
        console.log("Assets count:", assets.length);

        assets.forEach((asset: unknown, index: number) => {
          console.log(`Asset ${index + 1}:`, asset);
        });
      }

      try {
        console.log(
          "Serialized assets:",
          JSON.stringify(assets, null, 2)
        );
      } catch (serializationError) {
        console.warn(
          "[AI Engine] Assets could not be serialized:",
          serializationError
        );
      }

      console.groupEnd();

      // Handle the received assets
      this.handleAiGeneratedAssets(assets);

      console.log(
        "[AI Engine][Use selected] handleAiGeneratedAssets completed"
      );
    } catch (error) {
      console.error("[AI Engine] Error mounting AI window:", error);
      // The service will handle showing the popup for AI engine access errors
      // Only show snackbar for other types of errors
      this.processingMessage = 'Failed to load AI content generator.';
    } finally {
      /*
      * Restore original request handlers after assets are returned
      * or the modal fails.
      */
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.open = originalXhrOpen;
      XMLHttpRequest.prototype.send = originalXhrSend;

      console.log("[AI Engine] Request interceptors restored");
      this.isProcessing = false;
    }
  }

  private handleAiGeneratedAssets(assets: any): void {
    if (!assets || !Array.isArray(assets) || assets.length === 0) {
      return;
    }

    // For now, just show a success message
    this.coreService.showSnackbar(`${assets.length} AI-generated asset(s) created successfully.`);
  }

  private getCloudBaseUrl(): string {
    return environment.aiEngineUrl;
  }
}

// added one comment for testing purpose